// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FbtParam, fbt } from "fbt";
import {
  FedExConfirmationDetails,
  ProposedFedExPickupTime,
} from "components/Shipping/FedEx/FedExTypes";
import { TestRunStep, useTestRunState } from "store/testrun";
import {
  addDays,
  endOfDay,
  format,
  parse as parseDate,
  startOfDay,
} from "date-fns";

import DeclarativeList from "components/DeclarativeComponents/DeclarativeList";
import ExternalLink from "components/ui/ExternalLink";
import React from "react";
import { useFedExState } from "store/fedex";

function toHumanReadableDay(pickupWindow: ProposedFedExPickupTime): string {
  const startTime = parseDate(
    pickupWindow.date + " " + pickupWindow.startTime,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const tomorrowEnd = endOfDay(addDays(today, 1));

  if (startTime > todayStart && startTime <= todayEnd) {
    return fbt(
      "today",
      `Healthpulse end test: 
        day of pickup, as in 'package will be picked up today'`
    );
  }
  if (startTime > todayEnd && startTime <= tomorrowEnd) {
    return fbt(
      "tomorrow",
      `Healthpulse end test: 
        day of pickup, as in 'package will be picked up tomorrow'`
    );
  }
  return fbt(
    "on " + fbt.param("date", format(startTime, "MMMM do")),
    `Healthpulse end test: 
      day of pickup, as in 'package will be picked up on July 3rd'`
  );
}

function toHumanReadableTime(pickupWindow: ProposedFedExPickupTime) {
  const startTime = parseDate(
    pickupWindow.date + " " + pickupWindow.startTime,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
  const endTime = parseDate(
    pickupWindow.date + " " + pickupWindow.endTime,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
  const startTimeString = format(startTime, "h:mmaa").toLowerCase();
  const endTimeString = format(endTime, "h:mmaa").toLowerCase();

  return (
    <fbt
      desc={`Healthpulse end test: 
        time of pickup as in 'package will be picked up between 3:00pm and 4:00pm'`}
    >
      <strong>
        <FbtParam name="startTime">{startTimeString}</FbtParam>
      </strong>
      and
      <strong>
        <FbtParam name="endTime">{endTimeString}</FbtParam>
      </strong>
    </fbt>
  );
}

function getTranslatablePickupLocation(
  confirmationDetails: FedExConfirmationDetails | undefined
) {
  const packageLocation = confirmationDetails?.packageLocation;
  if (packageLocation) {
    if (packageLocation === "NONE" || packageLocation === "FRONT") {
      return (
        <strong>
          <fbt
            desc={`Healthpulse end test: 
              pickup location as in 'put your package in the front'`}
          >
            front
          </fbt>
        </strong>
      );
    }
    if (packageLocation === "REAR") {
      return (
        <strong>
          <fbt
            desc={`Healthpulse end test: 
              pickup location as in 'put your package in the rear'`}
          >
            rear
          </fbt>
        </strong>
      );
    }
    if (packageLocation === "SIDE") {
      return (
        <strong>
          <fbt
            desc={`Healthpulse end test: 
              pickup location as in 'put your package in the side'`}
          >
            side
          </fbt>
        </strong>
      );
    }
  }
  return (
    <fbt
      desc={`Healthpulse end test: 
        pickup location as in 'put your package in the pickup location'`}
    >
      pickup location
    </fbt>
  );
}

const EndTestScheduleDetails: React.FunctionComponent = () => {
  const testRunState = useTestRunState();
  const { confirmation: confirmationDetails } = useFedExState();
  const pickupDetails = testRunState.testRun?.scheduledPickup;
  let pickupDetailInfo = null;
  if (pickupDetails && testRunState.testRun?.registrationToken) {
    const pickupWindow = pickupDetails.pickupWindow;
    pickupDetailInfo = (
      <li>
        <fbt desc="Healthpulse end test: pickup detail">
          FedEx will pickup the package
          <strong>
            <FbtParam name="day">{toHumanReadableDay(pickupWindow)}</FbtParam>
          </strong>
          between
          <FbtParam name="timeWindow">
            {toHumanReadableTime(pickupWindow)}
          </FbtParam>. Place the package in the
          <FbtParam name="packageLocation">
            {getTranslatablePickupLocation(confirmationDetails)}
          </FbtParam>
          prior to your pickup window. You do not need to be present during
          pickup.
        </fbt>
      </li>
    );
  } else {
    const findDropOffVisit = testRunState.testRun?.steps["findDropOff"] as
      | TestRunStep
      | undefined;
    const callFedexVisit = testRunState.testRun?.steps["callFedex"] as
      | TestRunStep
      | undefined;
    if (findDropOffVisit || callFedexVisit) {
      if (
        callFedexVisit &&
        (!findDropOffVisit ||
          findDropOffVisit.lastVisitedTime < callFedexVisit.lastVisitedTime)
      ) {
        pickupDetailInfo = (
          <li>
            <fbt desc="Healthpulse end test: fedex pickup details">
              FedEx will pickup the package at the address and time you
              discussed on the phone. You do not need to be present during
              pickup.
            </fbt>
          </li>
        );
      } else if (
        findDropOffVisit &&
        (!callFedexVisit ||
          callFedexVisit.lastVisitedTime < findDropOffVisit.lastVisitedTime)
      ) {
        pickupDetailInfo = (
          <li>
            <fbt desc="Healthpulse end test: drop off details">
              Drop off the package at a FedEx drop box as soon as possible,
              preferably today. Samples arriving to the lab too late cannot be
              processed.
            </fbt>{" "}
            <ExternalLink href="http://fedex.com/labreturns">
              <fbt desc="">Locate a drop box</fbt>
            </ExternalLink>
          </li>
        );
      }
    }
  }
  return (
    <DeclarativeList>
      {pickupDetailInfo}
      <li>
        <fbt desc="Healthpulse end test: step detail">
          Once the specimen is received at the lab, it will be checked and
          processed. We will reach out if there are any issues.
        </fbt>
      </li>
      <li>
        <fbt desc="Healthpulse end test: step detail">
          An email will be sent when results are ready.
        </fbt>
      </li>
    </DeclarativeList>
  );
};

export default EndTestScheduleDetails;
